import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // A file upload must NOT carry the JSON content type set above.
  //
  // multipart/form-data is only parseable with the boundary string that
  // separates its parts, and that boundary is generated by the browser when it
  // serialises a FormData — but only if it is allowed to set the header
  // itself. Leaving `application/json` in place means the request goes out
  // labelled as JSON, the server's multipart parser finds no parts, and the
  // upload arrives with no file attached. The endpoint then reports the file
  // as missing, which is true and completely misleading: it was sent.
  //
  // Callers used to work around this individually with
  // `headers: { 'Content-Type': undefined }`, which every new upload had to
  // remember and the label cropper did not. Done here instead, once, where it
  // cannot be forgotten.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
    delete config.headers['content-type'];
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Handle responses
api.interceptors.response.use(
  // Callers get the body, which is what almost every call wants. File
  // downloads are the exception: the server puts the filename in a header, so
  // `{ fullResponse: true }` opts out of the unwrapping and keeps it.
  (response) => (response.config?.fullResponse ? response : response.data),
  (error) => {
    // A 401 means two completely different things, and treating them the same
    // is why a mistyped password wiped the login form.
    //
    //   signing in    → "those credentials are wrong". The person is already
    //                   on /login, there is no session to end, and the right
    //                   response is a message beside the form they are still
    //                   filling in.
    //   anywhere else → "your session has expired". Sign out and go to /login.
    //
    // The old code did the second for both: a wrong password triggered a full
    // page navigation to the page you were already on, which reloaded it and
    // threw away everything typed. Correcting one character became retyping
    // the address and the password.
    //
    // Identified by the ABSENCE of a session rather than by a list of URLs: no
    // token means nothing to expire, whatever the endpoint. The auth paths are
    // named as well for the case where a stale token is still in storage.
    const url = String(error.config?.url || '');
    const isSignIn = /\/auth\/(login|register|forgot-password|reset-password)/.test(url);
    const hadSession = Boolean(localStorage.getItem('token'));

    if (error.response?.status === 401 && !isSignIn && hadSession) {
      // An expired IMPERSONATION token means the admin's own session is parked
      // and still valid — put them back in it rather than signing them out.
      // Those tokens last an hour by design, so this is the ordinary way an
      // impersonated session ends, not an edge case.
      const adminToken = localStorage.getItem('adminToken');
      const adminUser = localStorage.getItem('adminUser');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');

      if (adminToken && adminUser) {
        localStorage.setItem('token', adminToken);
        localStorage.setItem('user', adminUser);
        window.location.href = '/admin/users';
        return Promise.reject(error.response?.data || error);
      }

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error.response?.data || error);
  }
);

export { API_BASE_URL };
export default api;
